import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Landmark, QrCode, Divide } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => new Intl.NumberFormat('de-AT', { style: 'currency', currency: 'EUR' }).format(v || 0);
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-AT') : '–');
const QUARTERS = [{ q: 1, due: '15.02.' }, { q: 2, due: '15.05.' }, { q: 3, due: '15.08.' }, { q: 4, due: '15.11.' }];
const EMPTY = { 1: '', 2: '', 3: '', 4: '' };
const num = (s) => { const v = parseFloat(String(s).replace(',', '.')); return Number.isFinite(v) ? v : NaN; };

/** ESt-Vorauszahlung je Mandant — vier individuell befüllbare Quartalsbeträge (Stichtage 15.2/15.5/15.8/15.11). */
export default function LexEstVzPanel({ clientId }) {
  const [data, setData] = useState({ items: [], runs: [], next_due: null });
  const [year, setYear] = useState(new Date().getFullYear());
  const [quarters, setQuarters] = useState(EMPTY);
  const [split, setSplit] = useState('');
  const [bescheid, setBescheid] = useState('');
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/lex/advisor/clients/${clientId}/est-vz`);
      setData(d);
      const cur = d.items.find((i) => i.year === year);
      if (cur) {
        setQuarters({ 1: String(cur.quarters['1'] || ''), 2: String(cur.quarters['2'] || ''), 3: String(cur.quarters['3'] || ''), 4: String(cur.quarters['4'] || '') });
        setBescheid(cur.bescheid_date || '');
      } else { setQuarters(EMPTY); setBescheid(''); }
    } catch { /* Panel bleibt leer */ }
  }, [clientId, year]);

  useEffect(() => { load(); }, [load]);

  const current = data.items.find((i) => i.year === year);
  const values = QUARTERS.map(({ q }) => (quarters[q] === '' ? 0 : num(quarters[q])));
  const invalid = values.some((v) => Number.isNaN(v) || v < 0);
  const total = invalid ? 0 : values.reduce((a, b) => a + b, 0);
  const nextDue = data.next_due;
  const nextDueRun = nextDue && data.runs.find((r) => r.evz_year === nextDue.year && r.evz_quarter === nextDue.quarter && ['approved', 'paid'].includes(r.status));
  const nextDueAmount = nextDue ? (data.items.find((i) => i.year === nextDue.year)?.quarters?.[String(nextDue.quarter)] || 0) : 0;

  const applySplit = () => {
    const v = num(split);
    if (!Number.isFinite(v) || v < 0) { toast.error('Bitte einen gültigen Jahresbetrag eingeben.'); return; }
    const per = String(Math.round((v / 4) * 100) / 100);
    setQuarters({ 1: per, 2: per, 3: per, 4: per });
    setSplit('');
  };

  const save = async () => {
    if (invalid) { toast.error('Bitte gültige Quartalsbeträge eingeben.'); return; }
    setSaving(true);
    try {
      const payload = Object.fromEntries(QUARTERS.map(({ q }, i) => [String(q), values[i]]));
      await axios.put(`${API}/lex/advisor/clients/${clientId}/est-vz`, { year, quarters: payload, bescheid_date: bescheid || null });
      toast.success(`ESt-Vorauszahlung ${year} gespeichert – ${fmtEur(total)} gesamt`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const approveStandalone = async () => {
    if (!nextDue) return;
    setApproving(true);
    try {
      await axios.post(`${API}/lex/advisor/clients/${clientId}/est-vz/${nextDue.year}/${nextDue.quarter}/approve`);
      toast.success(`Zahlungsanweisung ESt-VZ Q${nextDue.quarter}/${nextDue.year} an den Mandanten gesendet`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Zahlungsanweisung konnte nicht erzeugt werden');
    } finally { setApproving(false); }
  };

  return (
    <div className="lex-card p-6" data-testid="lex-mandat-estvz-card">
      <div className="flex items-center gap-2 mb-5">
        <Landmark className="h-4 w-4" style={{ color: '#C5A059' }} />
        <h2 className="text-xs uppercase tracking-widest font-semibold text-slate-500">Einkommensteuer-Vorauszahlung</h2>
      </div>

      <div className="grid gap-3 sm:grid-cols-[110px_1fr_auto] sm:items-end">
        <div>
          <label className="text-xs text-slate-500" htmlFor="estvz-year">Jahr</label>
          <Input id="estvz-year" type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value, 10) || year)} className="lex-mono" data-testid="lex-estvz-year" />
        </div>
        <div>
          <label className="text-xs text-slate-500" htmlFor="estvz-bescheid">Bescheid vom (optional)</label>
          <Input id="estvz-bescheid" type="date" value={bescheid} onChange={(e) => setBescheid(e.target.value)} className="lex-mono" data-testid="lex-estvz-bescheid" />
        </div>
        <div className="flex items-end gap-2">
          <div>
            <label className="text-xs text-slate-500" htmlFor="estvz-split">Jahresbetrag gleichmäßig verteilen</label>
            <Input id="estvz-split" type="number" step="0.01" inputMode="decimal" value={split} onChange={(e) => setSplit(e.target.value)} placeholder="z. B. 10000" className="lex-mono" data-testid="lex-estvz-split-amount" />
          </div>
          <Button type="button" variant="outline" onClick={applySplit} disabled={!split} className="rounded-none text-xs" title="Jahresbetrag durch 4 teilen und in alle Quartale eintragen" data-testid="lex-estvz-split-btn">
            <Divide className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="mt-4 grid gap-3 grid-cols-2 sm:grid-cols-4" data-testid="lex-estvz-quarters">
        {QUARTERS.map(({ q, due }) => (
          <div key={q} className="rounded border border-slate-200 bg-slate-50 p-3">
            <label className="flex items-baseline justify-between text-xs text-slate-500" htmlFor={`estvz-q${q}`}>
              <span className="font-semibold text-slate-700">Q{q}</span><span className="lex-mono">{due}{year}</span>
            </label>
            <Input id={`estvz-q${q}`} type="number" step="0.01" inputMode="decimal" value={quarters[q]}
              onChange={(e) => setQuarters((p) => ({ ...p, [q]: e.target.value }))} placeholder="0,00"
              className="lex-mono mt-1 bg-white" data-testid={`lex-estvz-q${q}`} />
          </div>
        ))}
      </div>

      <div className="mt-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="text-sm text-slate-700 lex-mono" data-testid="lex-estvz-total">
          Jahressumme <strong>{fmtEur(total)}</strong>
          {current?.updated_by_name && <span className="block text-xs text-slate-400 mt-0.5">Zuletzt geändert von {current.updated_by_name} am {fmtDate(current.updated_at)}</span>}
        </div>
        <Button onClick={save} disabled={saving || invalid} className="rounded-none text-xs uppercase tracking-wider" data-testid="lex-estvz-save">{saving ? 'Speichern…' : 'Speichern'}</Button>
      </div>

      <p className="mt-3 text-xs text-slate-500" data-testid="lex-estvz-hint">
        Der jeweilige Quartalsbetrag wird am Stichtag automatisch mit der UVA in <strong>einer</strong> Zahlungsanweisung (QR + Verwendungszweck „…U …E“) zusammengefasst. Quartale mit 0,00 € werden nicht angewiesen.
      </p>

      {nextDue && (
        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm" data-testid="lex-estvz-next">
          <span className="text-slate-600">Nächster Stichtag: <strong className="lex-mono">{fmtDate(nextDue.due_date)}</strong> (ESt-VZ Q{nextDue.quarter}/{nextDue.year}
            {nextDueAmount > 0 && <span className="lex-mono"> · {fmtEur(nextDueAmount)}</span>})
            {nextDueRun && <span className="ml-2 text-emerald-700">· Zahlungsanweisung erstellt ({nextDueRun.status === 'paid' ? 'bezahlt' : 'offen'})</span>}
          </span>
          {!nextDueRun && (
            <Button variant="outline" size="sm" onClick={approveStandalone} disabled={approving || nextDueAmount <= 0} title={nextDueAmount > 0 ? 'Nur nötig, wenn am Stichtag keine UVA fällig ist' : `Zuerst Betrag für Q${nextDue.quarter}/${nextDue.year} speichern`} data-testid="lex-estvz-approve-standalone">
              <QrCode className="h-4 w-4 mr-1.5" /> {approving ? 'Erzeuge…' : 'Nur ESt-VZ anweisen (ohne UVA)'}
            </Button>
          )}
        </div>
      )}

      {data.runs.length > 0 && (
        <table className="mt-5 w-full text-sm" data-testid="lex-estvz-runs">
          <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400"><th className="py-1 pr-3">Anweisung</th><th className="pr-3">Fällig</th><th className="text-right pr-3">USt</th><th className="text-right pr-3">ESt-VZ</th><th className="text-right pr-4">Gesamt</th><th>Status</th></tr></thead>
          <tbody className="lex-mono">
            {data.runs.map((r) => (
              <tr key={r.id} className="border-t border-slate-100">
                <td className="py-1.5 pr-3">{r.period_label}</td><td className="pr-3">{fmtDate(r.due_date)}</td>
                <td className="text-right pr-3">{fmtEur(r.ust_zahllast || 0)}</td><td className="text-right pr-3">{fmtEur(r.evz_amount)}</td>
                <td className="text-right pr-4 font-semibold">{fmtEur(r.zahllast)}</td>
                <td>{r.status === 'paid' ? <span className="text-emerald-700">bezahlt {fmtDate(r.paid_at)}</span> : 'offen'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
